import React, { useState, useEffect } from 'react';
import { Cable, Settings } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { DynamicContainer } from '../components/common/DynamicContainer';
// import { generateBreadcrumbPath } from './common/breadcrumbs'; // Commented out - breadcrumbs not needed
import { toast } from 'react-toastify';
import ParameterDrawer from './MasterConnectorDrawer/ParameterDrawer';
 
const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;
 
const MasterConnector = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);

  // Check if user is CDX user
  const isCdxUser = () => {
    try {
      const cdxUser = localStorage.getItem('cdx_user');
      return cdxUser === 'true';
    } catch (error) {
      console.error('Error reading cdx_user from localStorage:', error);
      return false;
    }
  };
 
  // Add useEffect to fetch data on component mount
  useEffect(() => {
    fetchConnectors();
  }, []);
 
  const fetchConnectors = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching connectors...'); // Debug log
      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
 
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
 
      const data = await response.json();
      console.log('Response:', data); // Debug log
 
      // Map the response data to match your column definitions and add an id field
      const mappedData = data.map(item => ({
        ...item,
        // Use mcId for CRUD operations - this is the UUID
        id: item.mcId,
      }));
 
      console.log('Mapped data for grid:', mappedData);
      setConnectors(mappedData);
 
      return mappedData;
    } catch (error) {
      console.error('Error details:', error); // Debug log
      toast.error(`Error fetching connectors: ${error.message}`);
      return [];
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleCreate = async (data) => {
    try {
      // Remove empty fields or null values that might cause validation issues
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v !== null && v !== '')
      );
 
      console.log('Creating connector with clean data:', cleanData);
 
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cleanData)
      });
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
 
      const responseData = await response.json();
      console.log('Create response:', responseData);
      toast.success('Connector created successfully');
      await fetchConnectors();
      return responseData;
    } catch (error) {
      console.error('Create error full details:', error);
      toast.error(`Error creating connector: ${error.message}`);
      throw error;
    }
  };
 
  const handleUpdate = async (id, data) => {
    try {
      // Using mcId for update operations
      console.log('Updating connector:', id, data);

      // Strip UI-only / lock fields. Blank strings must not wipe DB values
      // (Auth Type "" overwrote OAUTH2 when select options didn't match).
      const cleanData = Object.fromEntries(
        Object.entries(data || {}).filter(([key, v]) => {
          if (key === 'id' || key === 'version' || key === 'recId') return false;
          if (v === null || v === undefined || v === '') return false;
          return true;
        })
      );
 
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cleanData)
      });
 
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
 
      const responseData = await response.json();
      console.log('Update response:', responseData);
      toast.success('Connector updated successfully');
      await fetchConnectors();
      return responseData;
    } catch (error) {
      console.error('Update error full details:', error);
      toast.error(`Error updating connector: ${error.message}`);
      throw error;
    }
  };
 
  const handleDelete = async (id) => {
    try {
      // Using mcId for delete operations
      console.log('Deleting connector:', id);
 
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });
 
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
 
      toast.success('Connector deleted successfully');
      await fetchConnectors();
      return true;
    } catch (error) {
      console.error('Delete error full details:', error);
      toast.error(`Error deleting connector: ${error.message}`);
      throw error;
    }
  };
 
  // Function to open the parameter drawer
  const handleOpenParameterDrawer = (connector) => {
    setSelectedConnector(connector);
    setDrawerOpen(true);
  };
 
  // Updated columns with Parameter action button
  const columns = [
    { field: 'mcId', headerName: 'MC ID', width: 70 },
    { field: 'mcName', headerName: 'Name', width: 150 },
    { field: 'mcConnectorType', headerName: 'Connector Type', width: 180 },
    { field: 'mcHost', headerName: 'Host', width: 200 },
    { field: 'mcBaseurl', headerName: 'Base URL', width: 200 },
    { field: 'mcAuthType', headerName: 'Auth Type', width: 120 },
    { field: 'mcPort', headerName: 'Port', width: 100 },
    { field: 'mcAuthurl', headerName: 'Auth URL', width: 200 },
    { field: 'mcTokenurl', headerName: 'Token URL', width: 200 },
    { field: 'mcRefreshUrl', headerName: 'Refresh URL', width: 200 },
    { field: 'mcScope', headerName: 'Scope', width: 150 },
    { field: 'version', headerName: 'Version', width: 100 },
    {
      field: 'actions',
      headerName: 'Parameters',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Manage Parameters">
          <IconButton
            onClick={(e) => {
              e.stopPropagation(); // Prevent row selection
              handleOpenParameterDrawer(params.row);
            }}
            size="small"
          >
            <Settings fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ];
 
  // Form fields remain the same
  const formFields = [
    {
      name: 'mcName',
      label: 'Name',
      required: true,
      validate: (value) => {
        if (!value) return 'Name is required';
        if (value.length < 3) return 'Name must be at least 3 characters';
        return null;
      }
    },
    {
      name: 'mcConnectorType',
      label: 'Connector Type',
      type: 'select',
      options: [
        'SALESFORCE',
        'AUTODESK',
        'AUTODESK_DOCS',
        'QUICKBOOKS',
        'VAULT',
        'FUSION_OPERATION',
        'DYNAMICS_BC',
        'UNANET',
        'MINIO_STORAGE',
        'AWS_S3_STORAGE',
        'EGNYTE_STORAGE',
        'GOOGLE_DRIVE_STORAGE',
        'SHAREPOINT_STORAGE',
        'DROPBOX_STORAGE',
        'LITMUS',
        'ASROTRACK',
        'SNOWFLAKE',
        'SYNCTRACK',
        'CONDUCTOR'
      ]
    },
    {
      name: 'mcHost',
      label: 'Host'
    },
    {
      name: 'mcBaseurl',
      label: 'Base URL'
    },
    {
      name: 'mcAuthType',
      label: 'Auth Type',
      type: 'select',
      // Values must match what SyncTrack stores (e.g. AsroTrack uses OAUTH2).
      options: ['BASIC', 'OAUTH2', 'API_KEY', 'TOKEN']
    },
    {
      name: 'mcPort',
      label: 'Port',
      validate: (value) => {
        if (value && !/^\d+$/.test(value)) return 'Port must be a number';
        return null;
      }
    },
    {
      name: 'mcAuthurl',
      label: 'Auth URL'
    },
    {
      name: 'mcTokenurl',
      label: 'Token URL'
    },
    {
      name: 'mcRefreshUrl',
      label: 'Refresh URL'
    },
    {
      name: 'mcScope',
      label: 'Scope'
    },
    {
      name: 'version',
      label: 'Version',
      disabled: true,
      disableOnEdit: true,
      hideOnCreate: true
    }
  ];
 
  const initialFormData = {
    mcName: '',
    mcHost: null,
    mcBaseurl: null,
    mcAuthType: null,
    mcPort: null,
    mcAuthurl: null,
    mcTokenurl: null,
    mcRefreshUrl: null,
    mcScope: null,
    version: null
  };
 
  // const breadcrumbPaths = generateBreadcrumbPath('masterconnector'); // Commented out - breadcrumbs not needed
 
  return (
    <>
      <DynamicContainer
        title="Master Connector"
        icon={Cable}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        // Using mcId (UUID) consistently as the ID field
        idField="id"
        // breadcrumbPaths={breadcrumbPaths} // Commented out - breadcrumbs not needed
        rows={connectors}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        isLoading={isLoading}
        isCdxUser={isCdxUser()}
        functionCode="Master Connector"
      />
      <ParameterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        connectorId={selectedConnector?.id}  // Using mcId (UUID)
        connectorName={selectedConnector?.mcName}
      />
    </>
  );
};
 
export default MasterConnector;